<script setup lang="ts">
import { computed } from "vue";

import { objectiveActions } from "@/action/objectiveActions";
import StatusClassDistributionBar from "@/components/StatusDistribution/StatusClassDistributionBar.vue";
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import type { BoardWithObjectives } from "@/model/board";
import type { Objective, RAG } from "@/model/objective";
import { useObjectiveStore } from "@/store/objective";
import { useWorkModeStore } from "@/store/workMode";

import ObjectiveLinksChip from "./ObjectiveLinksChip.vue";
import RAGChip from "./RAGChip.vue";

const props = defineProps<{
  objective: Objective;
  board: BoardWithObjectives;
  hasStatusDistribution?: boolean;
  boardScale?: boolean;
  compact?: boolean;
}>();

const linkedCardTree = computed(() =>
  useObjectiveStore().linkedCardTree(props.board, props.objective),
);
const isExecutionMode = computed(() => useWorkModeStore().isExecutionMode);

function setRAGValue(rag: RAG) {
  objectiveActions.setRag("modal", props.board.id, props.objective, rag);
}
</script>

<template>
  <RAGChip
    v-if="isExecutionMode"
    :rag="objective.rag"
    :class="boardScale ? 'board-scale' : 'fixed-scale'"
    :compact
    @update:rag="setRAGValue"
  />
  <MenuItemDelimiter v-if="isExecutionMode" :compact />
  <ObjectiveLinksChip
    :linking-objective="{ type: 'objective', boardId: board.id, id: objective.id }"
    :class="boardScale ? 'board-scale' : 'fixed-scale'"
    :compact
  />
  <StatusClassDistributionBar
    v-if="hasStatusDistribution && isExecutionMode"
    :value="linkedCardTree.statusDistribution"
    :state="linkedCardTree.statusDistributionState"
    source-item-type="objective"
    :class="boardScale ? 'board-scale' : 'fixed-scale'"
    :compact
    :filters="{ boardId: board.id, objectiveId: objective.id }"
    :title="objective.text"
    :show-actions="false"
  />
</template>

<style lang="scss" scoped>
// Board-scale callers size the bar via `--scd-*` CSS vars instead.
.status-distribution.fixed-scale {
  height: 1rem;
  width: 30%;
  flex-grow: 1;
}
</style>
